/**
 * Service-worker registration, through `vite-plugin-pwa`'s own virtual module.
 *
 * Importing `virtual:pwa-register` is what stops the plugin injecting its
 * bare `registerSW.js`, which registers the worker and does nothing else: no
 * update check after the first load, and no reaction when a newer build has
 * finished installing (`docs/decisions/0046`).
 *
 * What this does:
 *
 * - registers at once (`immediate`), so an install never waits for `load`;
 * - asks the browser to look for a newer worker every time the page becomes
 *   visible again — a phone that reopens an installed PWA after a day is the
 *   case that matters, and it never does a navigation the browser would have
 *   checked on by itself;
 * - hands "a newer build is waiting" to `UpdateCoordinator`, which decides
 *   when the reload may happen (`update.ts`).
 *
 * The worker is built in `prompt` mode (`vite.config.ts`), so a new one WAITS
 * rather than taking over underneath a running game; `applyUpdate` below is
 * what tells it to go. `/api` and `/room` stay NetworkOnly and the app shell
 * stays precached exactly as before — nothing about what is cached changed,
 * so BASELINE 5 (offline solo) is untouched.
 *
 * Kept out of every unit-tested module on purpose: `virtual:pwa-register`
 * only exists inside a Vite build, so only `App.svelte` imports this file.
 */
import { registerSW } from 'virtual:pwa-register';

import { UpdateCoordinator } from './update.js';

let registration: ServiceWorkerRegistration | undefined;
/**
 * Filled in once `registerSW` has returned. A holder rather than a plain
 * binding because the coordinator (which calls it) and the registration
 * (which reports to the coordinator) each need the other to exist first.
 */
const worker: { applyUpdate?: (reloadPage?: boolean) => Promise<void> } = {};

/** The one coordinator for the page's life. `App.svelte` tells it which screen is showing. */
export const updates = new UpdateCoordinator(() => {
  // Sends SKIP_WAITING to the waiting worker; the virtual module reloads the
  // page once that worker has taken control.
  void worker.applyUpdate?.(true);
});

worker.applyUpdate = registerSW({
  immediate: true,
  onNeedRefresh() {
    updates.notifyWaiting();
  },
  onRegisteredSW(_scriptUrl, registered) {
    registration = registered;
  },
});

/** Ask the browser to look for a newer worker now. Never throws: offline is the ordinary case. */
export function checkForUpdate(): void {
  void registration?.update().catch(() => undefined);
}

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') checkForUpdate();
});
